/**
 * ============================================
 * REACT AUTH CONTEXT
 * ============================================
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthUser, getCurrentUser, login as apiLogin, logout as apiLogout, register as apiRegister } from '../services/authApi';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    villageOfOrigin?: string;
    isDiaspora?: boolean;
  }) => Promise<{ success: boolean; error?: string; requiresVerification?: boolean }>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    refreshUser();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth_success')) {
      refreshUser();
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('auth_error')) {
      console.error('OAuth error:', params.get('auth_error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  async function refreshUser() {
    setLoading(true);
    try {
      const result = await getCurrentUser();
      setUser(result.user || null);
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function login(email: string, password: string) {
    const result = await apiLogin(email, password);
    if (result.success && result.user) {
      setUser(result.user);
      return { success: true };
    }
    return { success: false, error: result.error };
  }

  async function logout() {
    await apiLogout();
    setUser(null);
  }

  async function register(data: Parameters<typeof apiRegister>[0]) {
    const result = await apiRegister(data);
    if (result.success && result.user) {
      setUser(result.user);
      return { success: true, requiresVerification: result.requiresVerification };
    }
    return { success: false, error: result.error };
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, register, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
